/**
 * Blood trails (1.06, owner decision, 29 October 2026: "once the health is below 25 after direct confrontation, player
 * bleeds trails for about 20 seconds unless his health is above 25 again, after, he just walks in bloody steps for until
 * is health is above 25. Walking on pools of blood, POOLS of blood, not blood steps also gives bloody steps for about 10
 * seconds."), after the idea's picture he chose (34, Blood trails: drops bright when fresh and darker as they dry, bloody
 * boot prints, "BLEEDING … 22 HP", "BANDAGING… THE TRAIL ENDS HERE").
 *
 * Another player's hit that leaves you under 25 health starts your bleeding: drops behind you for 20 seconds (a new hit
 * while you're under starts the 20 again), a puddle where you stand still; then bloody boot prints wherever you walk,
 * until you're healed above 25, which ends it all at once. A pool of blood (the battlefield's old ones, a body's, a
 * puddle a bleeding player left) gives anyone who steps in it bloody prints for 10 seconds, fading as the blood wears off
 * their boots; drops and prints are no pool. The server makes the marks (they're the others' to follow:
 * server/src/bloodTrails.ts), as decals on the ground everyone sees, each sent with its age, so the page dries it from
 * bright to dark as it was when it was made (client/src/scripts/managers/bloodTrails.ts).
 */
export const BloodTrail = Object.freeze({
    /** under this much health after another player's hit, you bleed; healed above it, it's over */
    wounded: 25,
    /** how long the drops fall after the hit that put you under (or the latest hit while you're under) */
    bleedMs: 20_000,
    /** stepping in a pool: bloody boots for this long after the last step in it */
    poolMs: 10_000,
    /** a few drops every so often while bleeding (ms): at 25 health the longer, near 0 the shorter */
    dropMs: [420, 200] as const,
    /** a drop as you're moving faster than this (units a ms) lands as a streak along your way */
    streakSpeed: 0.012,
    /** standing still (within `puddleStill` units) this long while bleeding leaves a puddle under you */
    puddleMs: 2600,
    puddleStill: 1.4,
    /** a print decal (two strides: four boots) every so many units walked */
    printStep: 6.4,
    /** further than this in one tick isn't a walk (a teleport, a spawn): no prints across it */
    jump: 12,
    /** how long each mark lies (ms) before it's gone; the last `fadeMs` of it, it fades */
    lifeMs: Object.freeze({ drop: 90_000, print: 45_000, puddle: 150_000 }),
    fadeMs: 8000,
    /** at most this many marks of one player's, and of everyone's (the oldest go first) */
    perPlayer: 90,
    perGame: 900,
    /** a body's pool (client/src/scripts/managers/gore.ts draws it, 4.7 units wide, spreading for 2.8 s): this wide, from a
     * moment after the death until the body is gone (4 minutes); none under a death by gas, fire or a lost connection */
    bodyPool: Object.freeze({ radius: 4, fromMs: 1500, untilMs: 240_000 }),
    /** how often (ticks) each player's feet are checked for a pool */
    poolCheckTicks: 4,
    /** a print's strength (its alpha) while you're wounded, and from a pool (it wears off over the 10 s) */
    woundPrint: 0.85,
    poolPrint: Object.freeze({ fresh: 1, worn: 0.32 })
});

/** the kinds of marks a trail leaves (common/src/definitions/decals.ts gives each picture its kind) */
export type TrailKind = keyof typeof BloodTrail.lifeMs;

/** the pools a foot can step in, by their decal (game units: inside the drawn edge) */
export const POOL_RADII: Readonly<Record<string, number>> = Object.freeze({
    gore_pool_1: 2.3,
    gore_pool_2: 2.9,
    trail_puddle_1: 1.95,
    trail_puddle_2: 2.25
});

/** under 25 health: wounded (a hit by another player then starts the bleeding) */
export const woundedAt = (health: number): boolean => health < BloodTrail.wounded;

/** healed above 25: the trail is over */
export const healedAt = (health: number): boolean => health > BloodTrail.wounded;

/** the time to the next drop (ms): the lower the health, the oftener; `random` in [0, 1) spreads them */
export function dropInterval(health: number, random: number): number {
    const [slow, fast] = BloodTrail.dropMs;
    const k = Math.min(1, Math.max(0, health / BloodTrail.wounded));
    const base = fast + (slow - fast) * k;
    return base * (0.75 + 0.5 * random);
}

/** a print's strength: steady while you're wounded; from a pool, wearing off over its 10 s (`left` ms of it left) */
export function printStrength(wounded: boolean, left: number): number {
    if (wounded) return BloodTrail.woundPrint;
    const { fresh, worn } = BloodTrail.poolPrint;
    const k = Math.min(1, Math.max(0, left / BloodTrail.poolMs));
    return worn + (fresh - worn) * k;
}

/** how see-through a mark is `age` ms into its life (1, then fading over its last `fadeMs`) */
export function trailFade(kind: TrailKind, age: number): number {
    const life = BloodTrail.lifeMs[kind];
    return Math.min(1, Math.max(0, (life - age) / BloodTrail.fadeMs));
}

/**
 * What a player's HUD shows of their trail (PlayerData.bloodTrail): seconds of bleeding left, the bloody prints they
 * leave (while wounded, or from a pool) and that pool's seconds left, and whether they're wounded
 */
export interface BloodTrailState {
    /** whole seconds of bleeding left (0: not bleeding) */
    readonly bleeding: number
    /** leaving bloody prints: none, because you're wounded (until you're healed above 25), or from a pool */
    readonly prints: TrailPrints
    /** a pool's prints: whole seconds left */
    readonly poolLeft: number
    /** under 25 after another player's hit, and not healed above it since */
    readonly wounded: boolean
}

export enum TrailPrints {
    None = 0,
    Wounded = 1,
    Pool = 2
}

export const NO_TRAIL: BloodTrailState = Object.freeze({ bleeding: 0, prints: TrailPrints.None, poolLeft: 0, wounded: false });

/** the state in two bytes: the prints (2 bits) and wounded (1), then the seconds (bleeding or the pool's, 0 to 31 each) */
export function trailToBytes(state: BloodTrailState): readonly [number, number] {
    const bleeding = Math.min(31, Math.max(0, Math.round(state.bleeding)));
    const pool = Math.min(31, Math.max(0, Math.round(state.poolLeft)));
    return [(state.prints & 3) | (state.wounded ? 4 : 0) | ((pool & 31) << 3), bleeding];
}

export function trailFromBytes(a: number, b: number): BloodTrailState {
    const prints = a & 3;
    return {
        prints: (prints === TrailPrints.Wounded || prints === TrailPrints.Pool ? prints : TrailPrints.None) as TrailPrints,
        wounded: (a & 4) !== 0,
        poolLeft: (a >> 3) & 31,
        bleeding: b & 31
    };
}

export const sameTrail = (a: BloodTrailState, b: BloodTrailState): boolean =>
    a.bleeding === b.bleeding && a.prints === b.prints && a.poolLeft === b.poolLeft && a.wounded === b.wounded;
